/* eslint-disable react/prop-types */
import { alpha, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { groupBy, isNil, isNumber, keyBy, orderBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useTreeItem } from '@mui/x-tree-view/useTreeItem'
import {
    TreeItemContent,
    TreeItemGroupTransition,
    TreeItemIconContainer,
    TreeItemLabel,
    TreeItemRoot,
    TreeItemCheckbox,
} from '@mui/x-tree-view/TreeItem'
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon'
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider'
import { useTreeItemModel } from '@mui/x-tree-view/hooks'
import DescriptionIcon from '@mui/icons-material/Description'
import PropTypes from 'prop-types'
import CommercialFeaturesSlice, { selectCommercialFeatures } from 'gdl/commercial/commercialFeature/CommercialFeaturesSlice'
import { productType } from '../ProductPropTypes'

const CustomLabel = ({ item, children, ...other }) => (
    <TreeItemLabel {...other}>
        <Grid container spacing={1} alignItems='center'>
            <Grid size='auto'>
                <Typography sx={{ fontSize: '1rem' }}>{children}</Typography>
            </Grid>
            {item.description && (
                <Grid size='auto'>
                    <Tooltip title={item.description}>
                        <DescriptionIcon />
                    </Tooltip>
                </Grid>
            )}
            <Grid size='grow' />
            <Grid size='auto'>
                <Typography sx={{ fontSize: '1rem' }}>{isNumber(item.price) ? `${item.price} €` : ''}</Typography>
            </Grid>
        </Grid>
    </TreeItemLabel>
)

const CustomTreeItem = React.forwardRef((props, ref) => {
    const {
        id, itemId, label, disabled, children, // for useTreeItem
        ...other // for getRootProps
    } = props

    const {
        getContextProviderProps,
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        status,
        getGroupTransitionProps,
        getCheckboxProps,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })
    const item = useTreeItemModel(itemId)

    return (
        <TreeItemProvider {...getContextProviderProps()}>
            <TreeItemRoot {...getRootProps(other)}>
                <TreeItemContent {...getContentProps()} sx={{ borderRadius: '8px' }}>
                    <TreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItemIcon status={status} slotProps={{ icon: { sx: { fontSize: '48px' } } }}/>
                    </TreeItemIconContainer>
                    <CustomLabel {...getLabelProps({ item })} />
                    {
                        item.mode === 'OPTION' && (
                            <TreeItemCheckbox {...getCheckboxProps({ indeterminate: item.indeterminate })}/>
                        )
                    }
                </TreeItemContent>
                {children && <TreeItemGroupTransition {...getGroupTransitionProps()}
                    sx={{
                        marginLeft: '15px',
                        paddingLeft: '8px',
                        borderLeft: `1px dashed ${alpha('#000000', 0.3)}`,
                    }}
                />}
            </TreeItemRoot>
        </TreeItemProvider>
    )
})

const ProductFeatureSelect = ({
    selectedIds = [],
    setSelectedIds = () => { },
    product = {},
}) => {
    const { featuresById } = useSelector(store => ({ featuresById: store.gdl.commercialFeatures.byId }), shallowEqual)

    const [expandedItems, setExpandedItems] = useState([])

    const formattedGroupedCommercialFeatures = useMemo(() => {
        if (isNil(product?.features)) return []
        const featuresWithMode = product.features.map(f => ({ ...featuresById[f.idFeature], mode: f.mode }))
        const orderedCommercialFeatures = orderBy(featuresWithMode, ['typeFeature', 'code'], ['desc', 'asc'])
        const groupedCommercialFeatures = groupBy(orderedCommercialFeatures, s => s.parent ?? 'headTree')

        const formatFunction = (filteredCommercialFeatures = [], depth) => {
            return filteredCommercialFeatures.map(({ id, name, description, price, mode }) => {
                const children = formatFunction(groupedCommercialFeatures[id], depth + 1)
                return {
                    id,
                    label: name,
                    price,
                    description,

                    children,
                    depth,
                    mode,
                }
            })
        }

        return formatFunction(groupedCommercialFeatures.headTree, 0)
    }, [featuresById, product])

    return (
        <Grid container spacing={1} alignItems='flex-start'>
            {formattedGroupedCommercialFeatures.map(commercialFeature => (
                <Grid key={commercialFeature.code} size={6}>
                    <Card>
                        <CardContent>
                            <RichTreeView
                                items={[commercialFeature]}
                                expandedItems={expandedItems}
                                onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
                                selectedItems={selectedIds}
                                onSelectedItemsChange={(_, ids) => {
                                    setSelectedIds(ids)
                                }}
                                slots={{ item: CustomTreeItem }}
                                itemChildrenIndentation={0}
                                // slotProps={{
                                //     item: {
                                //         indeterminate: true,
                                //     },
                                // }}
                                multiSelect
                                checkboxSelection
                            />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    )
}

ProductFeatureSelect.propTypes = {
    selectedIds: PropTypes.arrayOf(PropTypes.number),
    setSelectedIds: PropTypes.func,
    product: productType,
}

export default ProductFeatureSelect